import {
	computed,
	type ComputedRef,
	type MaybeRefOrGetter,
	onUnmounted,
	shallowReactive,
	toValue,
	watch,
} from "vue";

export interface MarkerMeta {}

export interface MarkerOptions {
	/**
	 * A unique id to identity this marker
	 */
	id: string;
	title?: string;
	optimized?: boolean;
	position: google.maps.LatLng | google.maps.LatLngLiteral;
	clickable?: boolean | null;
	draggable?: boolean | null;
	icon?: string | google.maps.Icon | null | google.maps.Symbol;
	meta?: MarkerMeta;
}

export type MarkerListeners = {
	click?: (evt: google.maps.MapMouseEvent, marker: MarkerOptions) => void;
	mouseover?: (evt: google.maps.MapMouseEvent, marker: MarkerOptions) => void;
	mouseout?: (evt: google.maps.MapMouseEvent, marker: MarkerOptions) => void;
};

export function useMarkers(
	markersOpts: MaybeRefOrGetter<MarkerOptions[]>,
	listeners: MarkerListeners = {}
): ComputedRef<google.maps.Marker[]> {
	// Must be shallowRef, otherwise, deep vue reactivity proxy break marker on map
	const markers = shallowReactive(new Map<string, google.maps.Marker>());

	function cleanupMarker(marker: google.maps.Marker) {
		marker.setMap(null);
		google.maps.event.clearInstanceListeners(marker);
	}

	watch(
		() => toValue(markersOpts),
		(markerOpts) => {
			const newMarkers = new Map(markerOpts.map((m) => [m.id, m]));

			const toAdd: google.maps.Marker[] = [];

			for (const [id, marker] of markers) {
				if (!newMarkers.has(id)) {
					markers.delete(id);
					cleanupMarker(marker);
				}
			}

			for (const opts of markerOpts) {
				const existingMarkers = markers.get(opts.id);
				if (existingMarkers != null) {
					existingMarkers.setOptions({
						position: opts.position,
						icon: opts.icon,
						clickable: opts.clickable,
					});
				} else {
					const newMarker = new google.maps.Marker({
						position: opts.position,
						icon: opts.icon,
						clickable: opts.clickable,
						// Optimization cause weird zoom glitch when using custom icons
						optimized: false,
					});

					newMarker.set("id", opts.id);
					newMarker.set("meta", opts.meta);

					for (const event in listeners) {
						if (listeners[event] == null) {
							continue;
						}

						newMarker.addListener(event, (evt: any) => listeners[event](evt, opts));
					}

					toAdd.push(newMarker);
					markers.set(opts.id, newMarker);
				}
			}
		},
		{ immediate: true }
	);

	onUnmounted(() => {
		for (const marker of markers.values()) {
			cleanupMarker(marker);
		}
	});

	return computed(() => Array.from(markers.values()));
}
